(() => {
  const theme = window.theme || {};
  const root = document.documentElement;
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  /* Money ----------------------------------------------------------------- */

  function formatMoney(cents) {
    const value = Number(cents) / 100;
    const format = theme.moneyFormat || '${{amount}}';
    const number = (decimals, thousands, decimal) => {
      const parts = value.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return number(0, ',', '.');
        case 'amount_with_comma_separator':
          return number(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return number(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return number(2, "'", '.');
        default:
          return number(2, ',', '.');
      }
    });
  }

  /* Analytics ------------------------------------------------------------- */

  // Publishes custom events to Shopify's customer events bus, where any
  // installed pixel (PostHog, GA4, Klaviyo…) can subscribe to them.
  function track(name, data = {}) {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish(name, data);
      }
    } catch (error) {
      // Analytics must never break the storefront.
    }
    document.dispatchEvent(new CustomEvent(`theme:${name}`, { detail: data }));
  }

  function toast(message) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toast.timer);
    toast.timer = setTimeout(() => {
      el.hidden = true;
    }, 4000);
  }

  /* Cart ------------------------------------------------------------------ */

  const Cart = {
    sectionId: 'cart-drawer',
    lastTrigger: null,

    get wrapper() {
      return document.getElementById(`shopify-section-${this.sectionId}`);
    },

    get onCartPage() {
      return theme.template === 'cart';
    },

    open(trigger) {
      if (this.onCartPage) return;
      this.lastTrigger = trigger || document.activeElement;
      root.classList.add('cart-open');
      const drawer = $('[data-cart-drawer]');
      if (drawer) drawer.focus({ preventScroll: true });
      track('cart_drawer_opened', { item_count: this.count() });
    },

    close() {
      root.classList.remove('cart-open');
      if (this.lastTrigger && this.lastTrigger.focus) this.lastTrigger.focus();
    },

    count() {
      const drawer = $('[data-cart-drawer]');
      return drawer ? Number(drawer.dataset.count) : 0;
    },

    setLoading(loading) {
      const body = $('[data-cart-body]');
      if (body) body.classList.toggle('is-loading', loading);
    },

    async request(url, payload) {
      const response = await fetch(`${url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections: this.sectionId, sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || theme.strings.error);
      return data;
    },

    async add(items, source) {
      const data = await this.request(theme.routes.cartAdd, { items });
      track('cart_item_added', { source, items });
      if (this.onCartPage) {
        window.location.reload();
        return;
      }
      this.render(data.sections);
      this.open();
    },

    async change(key, quantity, extra = {}) {
      this.setLoading(true);
      try {
        const data = await this.request(theme.routes.cartChange, { id: key, quantity, ...extra });
        track('cart_line_changed', { key, quantity });
        if (this.onCartPage) {
          window.location.reload();
          return;
        }
        this.render(data.sections);
      } catch (error) {
        toast(error.message);
      } finally {
        this.setLoading(false);
      }
    },

    async updateDiscounts(codes) {
      const data = await this.request(`${theme.routes.cart}/update`, { discount: codes.join(',') });
      return data;
    },

    async appliedCodes() {
      const cart = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      return (cart.discount_codes || []).filter((d) => d.applicable).map((d) => d.code);
    },

    discountMessage(text, isError) {
      $$('[data-discount-msg]').forEach((el) => {
        el.textContent = text;
        el.classList.toggle('is-error', Boolean(isError));
        el.hidden = !text;
      });
    },

    // Show the result after the page reloads (cart page) or the drawer re-renders.
    finishDiscount(data, text, isError) {
      if (this.onCartPage) {
        try {
          sessionStorage.setItem('pp_discount_msg', JSON.stringify({ text, isError }));
        } catch (error) {}
        window.location.reload();
        return;
      }
      this.render(data.sections);
      this.discountMessage(text, isError);
    },

    async applyDiscount(box) {
      const input = $('[data-discount-input]', box);
      const button = $('[data-discount-apply]', box);
      const code = input.value.trim();
      if (!code) {
        this.discountMessage(box.dataset.msgEmpty, true);
        input.focus();
        return;
      }
      button.classList.add('is-loading');
      try {
        const current = await this.appliedCodes();
        if (current.some((c) => c.toLowerCase() === code.toLowerCase())) {
          this.discountMessage(box.dataset.msgApplied.replace('CODE', code.toUpperCase()), false);
          return;
        }
        let data = await this.updateDiscounts([...current, code]);
        const result = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
        const applied = Boolean(result && result.applicable);
        track(applied ? 'cart_discount_applied' : 'cart_discount_rejected', { code });
        if (!applied) data = await this.updateDiscounts(current);
        const template = applied ? box.dataset.msgApplied : box.dataset.msgInvalid;
        this.finishDiscount(data, template.replace('CODE', code.toUpperCase()), !applied);
      } catch (error) {
        this.discountMessage(error.message, true);
      } finally {
        button.classList.remove('is-loading');
      }
    },

    async removeDiscount(code) {
      this.setLoading(true);
      try {
        const current = await this.appliedCodes();
        const data = await this.updateDiscounts(current.filter((c) => c.toLowerCase() !== code.toLowerCase()));
        track('cart_discount_removed', { code });
        this.finishDiscount(data, '', false);
      } catch (error) {
        toast(error.message);
      } finally {
        this.setLoading(false);
      }
    },

    render(sections) {
      const html = sections && sections[this.sectionId];
      if (!html || !this.wrapper) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${this.sectionId}`);
      if (!fresh) return;
      this.wrapper.innerHTML = fresh.innerHTML;

      const drawer = $('[data-cart-drawer]');
      const count = Number(drawer.dataset.count);
      theme.cartTotal = Number(drawer.dataset.total);
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.dataset.empty = count === 0;
      });
      $$('[data-cart-link]').forEach((el) => el.setAttribute('aria-label', `${el.dataset.label} (${count})`));
      document.dispatchEvent(new CustomEvent('theme:cart_updated'));
      if (root.classList.contains('cart-open')) drawer.focus({ preventScroll: true });
    },
  };

  /* Product page ---------------------------------------------------------- */

  function initProduct(section) {
    const dataEl = $('[data-product-data]', section);
    const form = $('[data-product-form]', section);
    if (!dataEl || !form) return;

    const data = JSON.parse(dataEl.textContent);
    const planSelect = $('[data-plan-select]', section);
    const quantityInput = $('[data-quantity]', section);
    const submit = $('[data-add-button]', section);
    const addLabel = $('[data-add-label]', section);
    const addCompare = $('[data-add-compare]', section);
    const sticky = $('[data-sticky-atc]', section);
    const stickyButton = $('[data-sticky-button]', section);
    const stickyDetail = $('[data-sticky-detail]', section);
    const notifyFields = $('[data-notify-fields]', section);
    const notifyBody = $('[data-notify-body]', section);
    const setText = (selector, text) => $$(selector, section).forEach((el) => (el.textContent = text));

    const currentVariant = () => {
      const id = Number(new FormData(form).get('id'));
      return data.variants.find((variant) => variant.id === id) || data.variants[0];
    };

    const isSubscription = () => {
      const checked = $('[name="purchase_option"]:checked', section);
      return Boolean(checked && checked.value === 'subscribe');
    };

    const showMedia = (mediaId) => {
      if (!mediaId) return;
      const target = $(`[data-media="${mediaId}"]`, section);
      if (!target) return;
      $$('[data-media]', section).forEach((el) => (el.hidden = el !== target));
      $$('[data-media-thumb]', section).forEach((el) =>
        el.setAttribute('aria-current', el.dataset.mediaThumb === String(mediaId) ? 'true' : 'false')
      );
    };

    const update = () => {
      const variant = currentVariant();
      const subscribing = isSubscription();
      const quantity = Math.max(1, Number(quantityInput.value) || 1);

      let planPrice = null;
      let planPercent = null;
      if (planSelect) {
        planSelect.disabled = !subscribing;
        const option = planSelect.options[planSelect.selectedIndex];
        planPrice = variant.plans[planSelect.value];
        planPercent = option ? option.dataset.percent : null;
      }

      // An automatic sale (theme settings) takes its percent off one-time prices until it ends; Shopify
      // rounds the discount down to the cent, so match that.
      const salePercent = data.sale_percent && Date.now() / 1000 < data.sale_ends ? data.sale_percent : 0;
      const oncePrice = variant.price - Math.floor((variant.price * salePercent) / 100);
      const unit = subscribing && planPrice != null ? planPrice : oncePrice;

      setText('[data-once-price]', formatMoney(oncePrice));
      setText('[data-once-compare]', oncePrice < variant.price ? formatMoney(variant.price) : '');
      // Before the shopper picks "Subscribe", advertise the best plan ("save up to X%", "from $Y").
      // Once subscribing, show the selected plan's own discount and price.
      const subLabelEl = $('[data-sub-label]', section);
      const maxPercent = subLabelEl && subLabelEl.dataset.maxPercent;
      const showUpTo = Boolean(maxPercent) && !subscribing;
      if (planPrice != null) {
        if (showUpTo) {
          const planPrices = Object.values(variant.plans || {});
          const fromPrice = planPrices.length ? Math.min(...planPrices) : planPrice;
          setText('[data-sub-price]', theme.strings.priceFrom.replace('[price]', formatMoney(fromPrice)));
          setText('[data-sub-compare]', fromPrice < variant.price ? formatMoney(variant.price) : '');
        } else {
          setText('[data-sub-price]', formatMoney(planPrice));
          setText('[data-sub-compare]', planPrice < variant.price ? formatMoney(variant.price) : '');
        }
      }
      setText(
        '[data-sub-label]',
        showUpTo
          ? theme.strings.subscribeSaveUpTo.replace('[percent]', maxPercent)
          : planPercent
            ? theme.strings.subscribeSave.replace('[percent]', planPercent)
            : theme.strings.subscribe
      );

      const label = variant.available
        ? `${theme.strings.addToCart} · ${formatMoney(unit * quantity)}`
        : theme.strings.soldOut;
      if (submit) submit.disabled = !variant.available;
      if (addLabel) addLabel.textContent = label;
      if (addCompare) {
        const discounted = variant.available && unit < variant.price;
        addCompare.hidden = !discounted;
        addCompare.textContent = discounted ? formatMoney(variant.price * quantity) : '';
      }
      if (stickyButton) {
        stickyButton.disabled = !variant.available;
        stickyButton.textContent = label;
      }
      if (stickyDetail) {
        const parts = [];
        if (data.variants.length > 1) parts.push(variant.title.toLowerCase());
        if (planSelect) {
          parts.push(subscribing ? planSelect.options[planSelect.selectedIndex].textContent.trim() : theme.strings.oneTime);
        }
        stickyDetail.textContent = parts.join(' · ');
      }
      $$('[data-when-available]', section).forEach((el) => (el.hidden = !variant.available));
      section.classList.toggle('is-unavailable', !variant.available);
      if (notifyFields) notifyFields.hidden = variant.available;
      if (notifyBody) notifyBody.value = `Back in stock request: ${notifyBody.dataset.product} (${variant.title})`;

      const shipMessage = $('[data-ship-message]', section);
      if (shipMessage && theme.freeShippingThreshold > 0) {
        const gap = theme.freeShippingThreshold - (theme.cartTotal || 0) - unit * quantity;
        shipMessage.textContent =
          gap > 0 ? theme.strings.shipsFreeGap.replace('[amount]', formatMoney(gap)) : theme.strings.shipsFree;
      }

      return { variant, subscribing, quantity, unit };
    };

    section.addEventListener('change', (event) => {
      const target = event.target;
      const state = update();

      if (target.name === 'id') {
        showMedia(state.variant.media_id);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', state.variant.id);
        window.history.replaceState({}, '', url);
        track('variant_selected', { product_id: data.id, variant_id: state.variant.id, title: state.variant.title });
      }
      if (target.name === 'purchase_option' || target === planSelect) {
        track('purchase_option_selected', {
          product_id: data.id,
          option: state.subscribing ? 'subscribe' : 'one_time',
          selling_plan: state.subscribing && planSelect ? Number(planSelect.value) : null,
        });
      }
    });

    section.addEventListener('click', (event) => {
      const step = event.target.closest('[data-quantity-step]');
      if (step) {
        const next = (Number(quantityInput.value) || 1) + Number(step.dataset.quantityStep);
        quantityInput.value = Math.min(99, Math.max(1, next));
        update();
        return;
      }
      const thumb = event.target.closest('[data-media-thumb]');
      if (thumb) showMedia(thumb.dataset.mediaThumb);
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const { variant, subscribing, quantity } = update();
      const item = { id: variant.id, quantity };
      if (subscribing && planSelect) item.selling_plan = Number(planSelect.value);

      submit.classList.add('is-loading');
      try {
        await Cart.add([item], 'product_page');
      } catch (error) {
        toast(error.message);
      } finally {
        submit.classList.remove('is-loading');
      }
    });

    // Show the sticky bar whenever the main add-to-cart button is off screen.
    if (sticky && submit && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        sticky.hidden = entry.isIntersecting;
        root.classList.toggle('has-sticky-atc', !entry.isIntersecting);
      }).observe(submit);
    }

    document.addEventListener('theme:cart_updated', update);
    update();
  }

  /* Bundle builder -------------------------------------------------------- */

  function initBundle(section) {
    const min = Number(section.dataset.min) || 3;
    const percent = Number(section.dataset.percent) || 0;
    const slots = $('[data-bundle-slots]', section);
    const message = $('[data-bundle-message]', section);
    const total = $('[data-bundle-total]', section);
    const compare = $('[data-bundle-compare]', section);
    const addButton = $('[data-bundle-add]', section);
    const picks = new Map();
    const fill = (text, count) => text.replace('[count]', count).replace('[percent]', percent);

    const render = () => {
      let count = 0;
      let subtotal = 0;
      const images = [];
      picks.forEach((quantity, item) => {
        count += quantity;
        subtotal += quantity * Number(item.dataset.price);
        for (let i = 0; i < quantity; i += 1) images.push(item.dataset.image);
        $('[data-bundle-qty]', item).textContent = quantity;
        item.classList.toggle('is-picked', quantity > 0);
      });

      const unlocked = count >= min;
      slots.textContent = '';
      for (let i = 0; i < Math.max(min, count); i += 1) {
        const slot = document.createElement('span');
        slot.className = 'bundle__slot';
        if (images[i]) {
          const img = document.createElement('img');
          img.src = images[i];
          img.alt = '';
          slot.classList.add('is-filled');
          slot.appendChild(img);
        }
        slots.appendChild(slot);
      }

      message.textContent = unlocked
        ? fill(section.dataset.messageDone, count)
        : fill(section.dataset.messageMore, min - count);
      const discounted = unlocked && percent > 0;
      total.textContent = formatMoney(discounted ? Math.round(subtotal * (100 - percent) / 100) : subtotal);
      compare.hidden = !discounted;
      compare.textContent = discounted ? formatMoney(subtotal) : '';
      addButton.disabled = !unlocked;
    };

    section.addEventListener('click', async (event) => {
      const step = event.target.closest('[data-bundle-step]');
      if (step) {
        const item = step.closest('[data-bundle-item]');
        const next = Math.max(0, (picks.get(item) || 0) + Number(step.dataset.bundleStep));
        picks.set(item, next);
        render();
        return;
      }

      if (event.target.closest('[data-bundle-add]')) {
        const items = [];
        picks.forEach((quantity, item) => {
          if (quantity > 0) items.push({ id: Number(item.dataset.variant), quantity, properties: { _box: 'Build a box' } });
        });
        addButton.classList.add('is-loading');
        try {
          await Cart.add(items, 'bundle_builder');
          picks.forEach((quantity, item) => picks.set(item, 0));
          render();
        } catch (error) {
          toast(error.message);
        } finally {
          addButton.classList.remove('is-loading');
        }
      }
    });

    render();
  }

  /* Product recommendations ------------------------------------------------ */

  function initRecommendations(el) {
    fetch(el.dataset.url)
      .then((response) => response.text())
      .then((html) => {
        const fresh = $('[data-recommendations]', new DOMParser().parseFromString(html, 'text/html'));
        if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  }

  /* Global events --------------------------------------------------------- */

  document.addEventListener('click', async (event) => {
    const target = event.target;

    const cartOpen = target.closest('[data-cart-open]');
    if (cartOpen && !Cart.onCartPage) {
      event.preventDefault();
      Cart.open(cartOpen);
      return;
    }

    if (target.closest('[data-cart-close]')) {
      Cart.close();
      return;
    }

    const quickAdd = target.closest('[data-quick-add]');
    if (quickAdd) {
      event.preventDefault();
      quickAdd.classList.add('is-loading');
      try {
        await Cart.add([{ id: Number(quickAdd.dataset.quickAdd), quantity: 1 }], quickAdd.dataset.source || 'quick_add');
      } catch (error) {
        toast(error.message);
      } finally {
        quickAdd.classList.remove('is-loading');
      }
      return;
    }

    const linePlan = target.closest('[data-line-plan]');
    if (linePlan) {
      event.preventDefault();
      const wrapper = linePlan.closest('.line-sub');
      const select = wrapper ? $('select', wrapper) : null;
      const plan = select ? Number(select.value) : null;
      track(plan ? 'cart_subscription_added' : 'cart_subscription_removed', { key: linePlan.dataset.key, selling_plan: plan });
      Cart.change(linePlan.dataset.key, Number(linePlan.dataset.quantity), { selling_plan: plan });
      return;
    }

    const discountApply = target.closest('[data-discount-apply]');
    if (discountApply) {
      event.preventDefault();
      Cart.applyDiscount(discountApply.closest('[data-cart-discount]'));
      return;
    }

    const discountRemove = target.closest('[data-discount-remove]');
    if (discountRemove) {
      event.preventDefault();
      Cart.removeDiscount(discountRemove.dataset.discountRemove);
      return;
    }

    const lineChange = target.closest('[data-line-change]');
    if (lineChange) {
      event.preventDefault();
      Cart.change(lineChange.dataset.key, Number(lineChange.dataset.quantity));
      return;
    }

    const copyLink = target.closest('[data-copy-link]');
    if (copyLink) {
      try {
        await navigator.clipboard.writeText(window.location.href.split('#')[0]);
        toast(copyLink.dataset.message);
        track('article_link_copied', { url: window.location.pathname });
      } catch (error) {
        toast(window.location.href);
      }
      return;
    }

    const menuOpen = target.closest('[data-menu-open]');
    if (menuOpen) {
      root.classList.add('menu-open');
      menuOpen.setAttribute('aria-expanded', 'true');
      const drawer = $('[data-menu-drawer]');
      if (drawer) drawer.focus({ preventScroll: true });
      return;
    }

    if (target.closest('[data-menu-close]')) {
      closeMenu();
      return;
    }

    // Close open header dropdowns when clicking elsewhere.
    $$('.nav-dropdown[open]').forEach((dropdown) => {
      if (!dropdown.contains(target)) dropdown.removeAttribute('open');
    });
  });

  function closeMenu() {
    if (!root.classList.contains('menu-open')) return;
    root.classList.remove('menu-open');
    const toggle = $('[data-menu-open]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }
  }

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (root.classList.contains('cart-open')) Cart.close();
    closeMenu();
    $$('.nav-dropdown[open]').forEach((dropdown) => dropdown.removeAttribute('open'));
  });

  document.addEventListener('submit', (event) => {
    if (event.target.matches('[data-newsletter-form]')) {
      track('newsletter_submitted', { location: event.target.dataset.newsletterForm || 'section' });
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-sort-select]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.delete('page');
      window.location.assign(url);
    }
  });

  /* Journal --------------------------------------------------------------- */

  function initJournal(section) {
    const chips = $$('[data-topic-filter]', section);
    if (!chips.length) return;
    section.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-topic-filter]');
      if (!chip) return;
      const topic = chip.dataset.topicFilter;
      chips.forEach((el) => el.setAttribute('aria-pressed', el === chip ? 'true' : 'false'));
      $$('[data-topic]', section).forEach((card) => {
        card.hidden = topic !== 'all' && card.dataset.topic !== topic;
      });
      track('journal_topic_selected', { topic: chip.textContent.trim() });
    });
  }

  function initArticle(section) {
    const content = $('[data-article-content]', section);
    const toc = $('[data-toc]', section);
    const list = $('[data-toc-list]', section);
    const bar = $('[data-reading-progress]');
    if (!content) return;

    // Table of contents from the article's H2s; each heading gets a linkable id.
    const headings = $$('h2', content);
    if (toc && list && headings.length >= 3) {
      const links = headings.map((heading, index) => {
        if (!heading.id) {
          heading.id = `${heading.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-${index + 1}`;
        }
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent.trim();
        item.appendChild(link);
        list.appendChild(item);
        return link;
      });
      toc.hidden = false;
      toc.open = window.matchMedia('(min-width: 990px)').matches;

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const index = headings.indexOf(entry.target);
              links.forEach((link, i) => link.classList.toggle('is-active', i === index));
            });
          },
          { rootMargin: '-20% 0px -70% 0px' }
        );
        headings.forEach((heading) => observer.observe(heading));
      }
    }

    if (bar) {
      let queued = false;
      const paint = () => {
        queued = false;
        const rect = content.getBoundingClientRect();
        const total = rect.height - window.innerHeight * 0.6;
        const progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
        bar.style.transform = `scaleX(${progress})`;
      };
      window.addEventListener(
        'scroll',
        () => {
          if (queued) return;
          queued = true;
          window.requestAnimationFrame(paint);
        },
        { passive: true }
      );
      paint();
    }
  }

  /* Email popup ----------------------------------------------------------- */

  function initPopup(dialog) {
    if (typeof dialog.showModal !== 'function') return;
    const key = 'pp-email-popup';
    const day = 24 * 60 * 60 * 1000;
    const read = () => {
      try {
        return JSON.parse(window.localStorage.getItem(key)) || {};
      } catch (error) {
        return {};
      }
    };
    const write = (value) => {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        // Storage can be unavailable in private windows; the popup still works.
      }
    };
    const open = (reason) => {
      if (dialog.open || root.classList.contains('cart-open') || root.classList.contains('menu-open')) return;
      dialog.showModal();
      track('email_popup_shown', { reason });
    };

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-popup-close]')) dialog.close();
    });
    dialog.addEventListener('close', () => {
      const state = read();
      if (!state.submitted) {
        write({ ...state, until: Date.now() + Number(dialog.dataset.days || 14) * day });
        track('email_popup_dismissed');
      }
    });
    dialog.addEventListener('submit', () => write({ ...read(), submitted: true }));

    // In the theme editor, open only while the section is selected.
    if (window.Shopify && Shopify.designMode) {
      const section = dialog.closest('.shopify-section');
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target === section) dialog.showModal();
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target === section && dialog.open) dialog.close();
      });
      return;
    }

    const state = read();
    if ($('[data-popup-success]', dialog)) {
      // Back from a successful signup: thank them once.
      if (state.submitted && !state.thanked) {
        write({ ...state, thanked: true });
        dialog.showModal();
      }
      return;
    }
    if (state.submitted || (state.until && state.until > Date.now())) return;
    if (dialog.dataset.customer || theme.template === 'cart') return;

    const timer = window.setTimeout(() => open('delay'), Number(dialog.dataset.delay || 20) * 1000);
    const stop = () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onExit);
    };
    function onScroll() {
      const scrolled = window.scrollY + window.innerHeight;
      if (scrolled >= document.documentElement.scrollHeight * 0.5 && window.scrollY > 400) {
        stop();
        open('scroll');
      }
    }
    function onExit(event) {
      if (!event.relatedTarget && event.clientY <= 0) {
        stop();
        open('exit');
      }
    }
    if (dialog.dataset.scroll === 'true') window.addEventListener('scroll', onScroll, { passive: true });
    if (dialog.dataset.exit === 'true') document.addEventListener('mouseout', onExit);
    dialog.addEventListener('close', stop);
  }

  // Sale prices are rendered on the server. If a cached page outlives the sale, switch back to the
  // regular price that sits right after each sale element.
  function expireSales(scope = document) {
    const now = Date.now() / 1000;
    $$('[data-sale-ends]', scope).forEach((el) => {
      if (now < Number(el.dataset.saleEnds)) return;
      el.hidden = true;
      const regular = el.nextElementSibling;
      if (regular && regular.hasAttribute('data-sale-off')) regular.hidden = false;
    });
  }

  function init(scope = document) {
    expireSales(scope);
    $$('[data-product-section]', scope).forEach(initProduct);
    $$('[data-recommendations][data-url]', scope).forEach(initRecommendations);
    $$('[data-bundle]', scope).forEach(initBundle);
    $$('[data-journal]', scope).forEach(initJournal);
    $$('[data-email-popup]', scope).forEach(initPopup);
    $$('[data-article]', scope).forEach(initArticle);
  }

  init();

  // Enter in the discount field applies the code instead of submitting checkout.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.matches('[data-discount-input]')) return;
    event.preventDefault();
    Cart.applyDiscount(event.target.closest('[data-cart-discount]'));
  });

  try {
    const flash = JSON.parse(sessionStorage.getItem('pp_discount_msg') || 'null');
    sessionStorage.removeItem('pp_discount_msg');
    if (flash && flash.text) Cart.discountMessage(flash.text, flash.isError);
  } catch (error) {}

  // Re-initialise sections when they are re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.theme.Cart = Cart;
  window.theme.formatMoney = formatMoney;
  window.theme.track = track;
})();
